import { useDateFormat, useProperty, useSiteContext, useTranslation } from '@eti/providers';
import { date, string } from '@eti/utils';
import {
  CHANGE_OF_AIRPORT,
  OVERNIGHT_STAY,
  SELF_TRANSFER,
  STOP,
  TRANSFER_TO_AIRPORT,
  TRANSFER_TO_BUS,
  TRANSFER_TO_TRAIN,
  ONE_DAY_STOPOVER,
  SHORT_STOPOVER,
  ONE_NIGHT_STOPOVER,
  TWO_NIGHTS_STOPOVER,
} from '../../../constants/eventSegmentTypes';
import * as tripConstants from '../../../constants/transportationMeansConstants';
import { getBoundDate, getBoundTitle, getLocalizedDuration } from '../../trip-details/utils/format';

export const useBoundHeaderParameters = (isMultiStop, segments, type, boundId) => {
  const { t } = useTranslation();
  const { weekday } = useDateFormat();
  const { language, timeFormat } = useSiteContext();
  const totalDuration = segments.reduce(
    (acc, { duration }) => (duration && !Number.isNaN(duration) ? acc + duration : acc),
    0,
  );
  return {
    calenderDate: string.firstToUpperCase(getBoundDate(segments, weekday)),
    title: t(getBoundTitle(type, isMultiStop), parseInt(boundId, 10) + 1),
    totalDuration:
      totalDuration &&
      t(
        'CartTripDetails.TotalDuration',
        `${getLocalizedDuration(totalDuration, language.code, timeFormat)}`,
      ),
  };
};

const useTransformTripSegment = (segment) => {
  const { weekday, time } = useDateFormat();
  const { language, timeFormat } = useSiteContext();
  const { t } = useTranslation();
  const { aircraftType, arrivedAt, departuredAt, duration, equipmentCode } = segment;
  const durationInDays = date.durationInDays(new Date(departuredAt), new Date(arrivedAt));
  const daysOrDayTanslation = durationInDays > 1 ? t('Days') : t('Day');

  return {
    ...segment,
    duration: getLocalizedDuration(duration, language.code, timeFormat),
    durationInDays: durationInDays ? `+${durationInDays} ${daysOrDayTanslation}` : null,
    arrivalDate: string.firstToUpperCase(date.format(arrivedAt, weekday)),
    arrivalTime: date.format(arrivedAt, time),
    departureTime: date.format(departuredAt, time),
    tripSegmentType:
      tripConstants[
        Object.keys(tripConstants).find((constant) => tripConstants[constant] === equipmentCode)
      ],
    transportType: aircraftType,
  };
};

const useTransformEventSegment = (segment, eventSegmentParameters) => {
  const { language, timeFormat } = useSiteContext();
  const { t } = useTranslation();
  const { p } = useProperty();
  const { nextAirport, recentAirport, recentDestination } = eventSegmentParameters;
  const { duration, types } = segment;
  const showVIIconsAndTextsBaggage = p('SelfTransfer.IconsAndText.Baggage.Enabled');
  const showVIIconsAndTextsTransfer = p('SelfTransfer.IconsAndText.Transfer.Enabled');
  const showVIIconsAndTextsGuarantee = p('SelfTransfer.IconsAndText.Guarantee.Enabled');
  const isSelfTransferDescriptionAvailable =
    showVIIconsAndTextsBaggage || showVIIconsAndTextsTransfer || showVIIconsAndTextsGuarantee;
  const isDescriptionAvailable = types.some(
    ({ type }) =>
      (type !== STOP && type !== SELF_TRANSFER) ||
      (type === SELF_TRANSFER && isSelfTransferDescriptionAvailable),
  );

  const eventSegmentTitles = {
    [STOP]: t(
      'CartTripDetails.Stop',
      getLocalizedDuration(duration, language.code, timeFormat),
      recentDestination,
    ),
    [OVERNIGHT_STAY]: t('TripDetails.Details.Overnight'),
    [CHANGE_OF_AIRPORT]: t('TripDetails.Details.ChangeOfAirport'),
    [TRANSFER_TO_AIRPORT]: t('TripDetails.Details.TransferToAirport'),
    [TRANSFER_TO_BUS]: t('TripDetails.Details.TransferToBus'),
    [TRANSFER_TO_TRAIN]: t('TripDetails.Details.TransferToTrain'),
    [SELF_TRANSFER]: t('SelfTransfer.Result.Header.Text'),
    [SHORT_STOPOVER]: t('TripDetails.Details.StopoverInRome.ShortStopover.Title'),
    [ONE_DAY_STOPOVER]: t('TripDetails.Details.StopoverInRome.OneDayStopover.Title'),
    [ONE_NIGHT_STOPOVER]: t('TripDetails.Details.StopoverInRome.OneNightStopover.Title'),
    [TWO_NIGHTS_STOPOVER]: t('TripDetails.Details.StopoverInRome.TwoNightsInRome.Title'),
  };
  const description = {
    [OVERNIGHT_STAY]: t('TripDetails.Details.Description.Overnight', recentDestination),
    [CHANGE_OF_AIRPORT]: t(
      'TripDetails.Details.Description.ChangeOfAirport',
      recentAirport,
      nextAirport,
    ),
    [TRANSFER_TO_AIRPORT]: t('TripDetails.Details.Description.TransferToAirport'),
    [TRANSFER_TO_BUS]: t('TripDetails.Details.Description.TransferToBus'),
    [TRANSFER_TO_TRAIN]: t('TripDetails.Details.Description.TransferToTrain'),
  };

  const icon = {
    [STOP]: 'stop',
    [OVERNIGHT_STAY]: 'bed',
    [CHANGE_OF_AIRPORT]: 'change',
    [TRANSFER_TO_AIRPORT]: 'change',
    [TRANSFER_TO_BUS]: 'change',
    [TRANSFER_TO_TRAIN]: 'change',
    [SELF_TRANSFER]: 'self-transfer',
  };

  return {
    description,
    isDescriptionAvailable,
    eventSegmentTitles,
    icon,
    isSelfTransferDescriptionAvailable,
    types,
  };
};

export const usetransformSegments = (segments) => {
  return segments.map((segment, i, allSegments) => {
    const eventSegmentParameters = {
      nextAirport: allSegments[i + 1]?.origin?.name,
      recentAirport: allSegments[i - 1]?.destination?.name,
      recentDestination: allSegments[i - 1]?.destination?.cityName,
    };
    return {
      hasNextStop: Boolean(allSegments[i + 1]),
      isTripSegment: segment.__typename === 'TripSegment',
      segment:
        segment.__typename === 'TripSegment'
          ? useTransformTripSegment(segment)
          : useTransformEventSegment(segment, eventSegmentParameters),
      segmentId: i,
    };
  });
};
